/**
 * Aurum - Shopify Theme JavaScript
 * Handles interactivity for header, testimonials, FAQ, etc.
 */

(function () {
  'use strict';

  // ==========================================================================
  // UTILITIES
  // ==========================================================================

  function parseBoolean(value, fallback = false) {
    if (value === 'true') return true;
    if (value === 'false') return false;
    return fallback;
  }

  function debounce(fn, delay) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  function themeString(key, fallback = '') {
    const strings = window.theme && window.theme.strings ? window.theme.strings : null;
    const value = strings && typeof strings[key] === 'string' ? strings[key] : '';
    return value || fallback;
  }

  function setInteractiveHiddenState(element, isHidden) {
    if (!(element instanceof HTMLElement)) return;

    element.setAttribute('aria-hidden', String(isHidden));

    if (isHidden) {
      element.setAttribute('inert', '');
      return;
    }

    element.removeAttribute('inert');
  }

  function getFocusableElements(container) {
    if (!(container instanceof HTMLElement)) return [];

    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    return Array.from(container.querySelectorAll(focusableSelectors)).filter((el) => {
      if (!(el instanceof HTMLElement)) return false;
      return el.getClientRects().length > 0;
    });
  }

  function trapFocusWithin(container, event) {
    if (!(event instanceof KeyboardEvent) || event.key !== 'Tab') return;

    const focusables = getFocusableElements(container);
    if (focusables.length === 0) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function focusFirstFocusable(container, fallback = null) {
    const target = getFocusableElements(container)[0] || fallback;
    if (target instanceof HTMLElement) target.focus();
  }

  // ==========================================================================
  // HEADER
  // ==========================================================================

  function initHeader() {
    const header = document.querySelector('[data-header]');
    if (!header) return;

    const handleScroll = debounce(() => {
      if (window.scrollY > 20) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }
    }, 10);

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
  }

  function initHeaderMegaMenu() {
    const dropdowns = Array.from(document.querySelectorAll('[data-header] [data-nav-dropdown]'));
    if (dropdowns.length === 0) return;

    dropdowns.forEach(details => {
      if (!(details instanceof HTMLDetailsElement)) return;
      if (details.dataset.megaMenuBound === 'true') return;
      details.dataset.megaMenuBound = 'true';

      details.addEventListener('toggle', () => {
        if (!details.open) return;
        dropdowns.forEach(other => {
          if (other === details) return;
          if (other instanceof HTMLDetailsElement) other.open = false;
        });
      });
    });

    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (target instanceof Element && target.closest('[data-nav-dropdown]')) return;

      dropdowns.forEach(details => {
        if (details instanceof HTMLDetailsElement) details.open = false;
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;

      dropdowns.forEach(details => {
        if (details instanceof HTMLDetailsElement) details.open = false;
      });
    });
  }

  // ==========================================================================
  // MOBILE MENU
  // ==========================================================================

  function initMobileMenu() {
    const toggle = document.querySelector('[data-mobile-menu-toggle]');
    const menu = document.querySelector('[data-mobile-menu]');
    const menuContent = menu ? menu.querySelector('.mobile-menu__content') : null;
    const closeButtons = document.querySelectorAll('[data-mobile-menu-close]');

    if (!toggle || !menu) return;
    let lastActiveElement = null;

    function openMenu(trigger = null) {
      lastActiveElement = trigger || document.activeElement;
      menu.classList.add('is-open');
      setInteractiveHiddenState(menu, false);
      toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';

      requestAnimationFrame(() => {
        focusFirstFocusable(menuContent, menuContent instanceof HTMLElement ? menuContent : null);
      });
    }

    function closeMenu() {
      menu.classList.remove('is-open');
      setInteractiveHiddenState(menu, true);
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';

      if (lastActiveElement instanceof HTMLElement) {
        lastActiveElement.focus();
      }
      lastActiveElement = null;
    }

    toggle.addEventListener('click', () => {
      const isOpen = menu.classList.contains('is-open');
      isOpen ? closeMenu() : openMenu(toggle);
    });

    closeButtons.forEach(btn => {
      btn.addEventListener('click', closeMenu);
    });

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && menu.classList.contains('is-open')) {
        closeMenu();
      }
    });

    menu.addEventListener('keydown', (event) => {
      if (!menu.classList.contains('is-open')) return;
      trapFocusWithin(menuContent, event);
    });

    setInteractiveHiddenState(menu, true);
  }

  // ==========================================================================
  // THEME TOGGLE
  // ==========================================================================

  function initThemeToggle() {
    const toggle = document.querySelector('[data-theme-toggle]');
    if (!toggle) return;

    const html = document.documentElement;
    const stored = localStorage.getItem('theme');

    if (stored === 'dark' || (!stored && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
      html.classList.add('dark');
    }

    toggle.addEventListener('click', () => {
      html.classList.toggle('dark');
      localStorage.setItem('theme', html.classList.contains('dark') ? 'dark' : 'light');
    });
  }

  // ==========================================================================
  // TESTIMONIALS CAROUSEL
  // ==========================================================================

  function initTestimonialsCarousel() {
    const carousel = document.querySelector('[data-testimonials-carousel]');
    if (!carousel) return;

    const slides = Array.from(carousel.querySelectorAll('[data-testimonial-slide]'));
    const dots = Array.from(carousel.querySelectorAll('[data-testimonial-dot]'));
    const prevBtn = carousel.querySelector('[data-testimonial-prev]');
    const nextBtn = carousel.querySelector('[data-testimonial-next]');
    const toggleBtn = carousel.querySelector('[data-testimonial-toggle]');
    const iconPause = toggleBtn?.querySelector('[data-testimonial-icon="pause"]') || null;
    const iconPlay = toggleBtn?.querySelector('[data-testimonial-icon="play"]') || null;
    const labelPause = toggleBtn?.dataset.labelPause || null;
    const labelPlay = toggleBtn?.dataset.labelPlay || null;

    if (slides.length === 0) return;

    let currentIndex = 0;
    let isAutoplayEnabled = slides.length > 1;
    let isPausedByPointer = false;
    let isPausedByFocus = false;
    let autoPlayId = null;
    const autoPlayMs = 6000;

    const updateToggleButton = () => {
      if (!toggleBtn) return;

      const ariaLabel = isAutoplayEnabled ? (labelPause || 'Pause') : (labelPlay || 'Play');
      toggleBtn.setAttribute('aria-label', ariaLabel);
      toggleBtn.setAttribute('aria-pressed', String(!isAutoplayEnabled));

      if (iconPause) iconPause.hidden = !isAutoplayEnabled;
      if (iconPlay) iconPlay.hidden = isAutoplayEnabled;
    };

    const showSlide = (index) => {
      slides.forEach((slide, i) => {
        const isActive = i === index;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
      });

      dots.forEach((dot, i) => {
        const isActive = i === index;
        dot.classList.toggle('is-active', isActive);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      currentIndex = index;
    };

    const nextSlide = () => {
      showSlide((currentIndex + 1) % slides.length);
    };

    const prevSlide = () => {
      showSlide((currentIndex - 1 + slides.length) % slides.length);
    };

    const updateRotation = () => {
      const shouldRotate = isAutoplayEnabled && !isPausedByPointer && !isPausedByFocus;

      if (shouldRotate && autoPlayId === null) {
        autoPlayId = setInterval(nextSlide, autoPlayMs);
      }

      if (!shouldRotate && autoPlayId !== null) {
        clearInterval(autoPlayId);
        autoPlayId = null;
      }

      carousel.setAttribute('aria-live', shouldRotate ? 'off' : 'polite');
      updateToggleButton();
    };

    if (prevBtn) prevBtn.addEventListener('click', prevSlide);
    if (nextBtn) nextBtn.addEventListener('click', nextSlide);

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => showSlide(i));
    });

    if (toggleBtn) {
      toggleBtn.addEventListener('click', () => {
        isAutoplayEnabled = !isAutoplayEnabled;
        updateRotation();
      });
    }

    carousel.addEventListener('mouseenter', () => {
      isPausedByPointer = true;
      updateRotation();
    });

    carousel.addEventListener('mouseleave', () => {
      isPausedByPointer = false;
      updateRotation();
    });

    carousel.addEventListener('focusin', () => {
      isPausedByFocus = true;
      updateRotation();
    });

    carousel.addEventListener('focusout', (event) => {
      const nextFocused = event.relatedTarget;
      if (nextFocused instanceof HTMLElement && carousel.contains(nextFocused)) return;
      isPausedByFocus = false;
      updateRotation();
    });

    showSlide(0);
    updateRotation();
  }

  // ==========================================================================
  // FAQ ACCORDION
  // ==========================================================================

  function initAccordions() {
    const accordions = document.querySelectorAll('[data-faq-accordion], [data-accordion]');
    if (accordions.length === 0) return;

    accordions.forEach(accordion => {
      if (accordion.dataset.accordionBound === 'true') return;
      accordion.dataset.accordionBound = 'true';

      const items = accordion.querySelectorAll('details[data-faq-item], details[data-accordion-item]');
      if (items.length === 0) return;

      items.forEach(item => {
        item.addEventListener('toggle', () => {
          if (!item.open) return;

          items.forEach(other => {
            if (other === item) return;
            other.open = false;
          });
        });
      });
    });
  }

  // ==========================================================================
  // SCROLL ANIMATIONS
  // ==========================================================================

  function initScrollAnimations() {
    const enableAnimations = parseBoolean(document.documentElement.dataset.enableAnimations, true);
    const respectReducedMotion = parseBoolean(document.documentElement.dataset.respectReducedMotion, true);
    const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!enableAnimations || (respectReducedMotion && prefersReducedMotion)) {
      document.querySelectorAll('[data-animate]').forEach(el => {
        el.classList.add('is-visible');
      });
      return;
    }

    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('[data-animate]').forEach(el => {
        el.classList.add('is-visible');
      });
      return;
    }

    document.documentElement.classList.add('scroll-animations');

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0, rootMargin: '0px 0px 200px 0px' });

    document.querySelectorAll('[data-animate]').forEach(el => {
      observer.observe(el);
    });
  }

  // ==========================================================================
  // SMOOTH SCROLL
  // ==========================================================================

  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function (e) {
        const targetId = this.getAttribute('href');
        if (targetId === '#') return;

        const target = document.querySelector(targetId);
        if (!target) return;

        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  // ==========================================================================
  // SEARCH
  // ==========================================================================

  function initSearchDrawer() {
    const drawer = document.querySelector('[data-search-drawer]');
    if (!drawer) return;

    const openButtons = Array.from(document.querySelectorAll('[data-search-drawer-open]'));
    const closeButtons = drawer.querySelectorAll('[data-search-drawer-close]');
    const panel = drawer.querySelector('.search-drawer__panel');
    const input = drawer.querySelector('[data-search-drawer-input]');
    const resultsEl = drawer.querySelector('[data-search-drawer-results]');
    const statusEl = drawer.querySelector('[data-search-drawer-status]');

    const suggestUrl = drawer.dataset.searchSuggestUrl || '';
    const types = drawer.dataset.searchTypes || 'product';
    const limit = Number.parseInt(drawer.dataset.searchLimit || '6', 10);
    const startText = drawer.dataset.textStart || '';
    const noResultsTemplate = drawer.dataset.textNoResults || '';
    const productsLabel = drawer.dataset.labelProducts || 'Products';
    const collectionsLabel = drawer.dataset.labelCollections || 'Collections';
    const pagesLabel = drawer.dataset.labelPages || 'Pages';
    const articlesLabel = drawer.dataset.labelArticles || 'Articles';

    let lastActiveElement = null;
    let abortController = null;

    const setTriggerExpanded = (isExpanded) => {
      openButtons.forEach(btn => {
        btn.setAttribute('aria-expanded', String(isExpanded));
      });
    };

    const focusInput = () => {
      if (input instanceof HTMLInputElement) input.focus();
    };

    function openDrawer(trigger = null) {
      lastActiveElement = trigger || document.activeElement;
      drawer.classList.add('is-open');
      setInteractiveHiddenState(drawer, false);
      setTriggerExpanded(true);
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(focusInput);
    }

    function closeDrawer() {
      drawer.classList.remove('is-open');
      setInteractiveHiddenState(drawer, true);
      setTriggerExpanded(false);
      document.body.style.overflow = '';

      if (abortController) abortController.abort();
      abortController = null;

      if (resultsEl) resultsEl.innerHTML = '';
      if (statusEl) statusEl.textContent = startText;

      if (lastActiveElement instanceof HTMLElement) lastActiveElement.focus();
      lastActiveElement = null;
    }

    window.openSearchDrawer = openDrawer;
    window.closeSearchDrawer = closeDrawer;

    openButtons.forEach(btn => {
      if (btn.dataset.searchDrawerOpenBound === 'true') return;
      btn.dataset.searchDrawerOpenBound = 'true';

      btn.addEventListener('click', (event) => {
        event.preventDefault();
        openDrawer(btn);
      });
    });

    closeButtons.forEach(btn => {
      if (btn.dataset.searchDrawerCloseBound === 'true') return;
      btn.dataset.searchDrawerCloseBound = 'true';
      btn.addEventListener('click', closeDrawer);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (!drawer.classList.contains('is-open')) return;
      closeDrawer();
    });

    // Focus trap
    if (drawer.dataset.searchDrawerFocusTrapBound !== 'true') {
      drawer.dataset.searchDrawerFocusTrapBound = 'true';

      drawer.addEventListener('keydown', (event) => {
        if (!drawer.classList.contains('is-open')) return;
        trapFocusWithin(panel, event);
      });
    }

    setInteractiveHiddenState(drawer, true);

    const escapeHtml = (value) => {
      const text = String(value == null ? '' : value);
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    };

    const renderGroup = (heading, items, renderItem) => {
      if (!items || items.length === 0) return '';
      const list = items.map(renderItem).join('');
      return `
        <div class="predictive-search__group">
          <p class="predictive-search__heading">${escapeHtml(heading)}</p>
          <ul class="predictive-search__list" role="list">${list}</ul>
        </div>
      `;
    };

    const renderLinkItem = (item) => {
      return `
        <li>
          <a class="predictive-search__item" href="${escapeHtml(item.url)}">
            <span class="predictive-search__title">${escapeHtml(item.title)}</span>
          </a>
        </li>
      `;
    };

    const renderProductItem = (product) => {
      const image = product.image || product.featured_image;
      const imageUrl = image && (image.url || image);
      const priceCents = Number(product.price);
      const compareCents = Number(product.compare_at_price);
      const hasPrice = Number.isFinite(priceCents);
      const hasCompare = Number.isFinite(compareCents) && compareCents > 0;
      const onSale = hasPrice && hasCompare && compareCents > priceCents;

      const price = hasPrice ? formatMoney(priceCents) : '';
      const compareAt = onSale ? formatMoney(compareCents) : '';

      return `
        <li>
          <a class="predictive-search__item predictive-search__item--product" href="${escapeHtml(product.url)}">
            <span class="predictive-search__media">
              ${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="" loading="lazy" width="44" height="44">` : ''}
            </span>
            <span class="predictive-search__text">
              <span class="predictive-search__title">${escapeHtml(product.title)}</span>
              ${price ? `<span class="predictive-search__meta">${onSale ? `<span class="predictive-search__compare">${escapeHtml(compareAt)}</span>` : ''}<span class="predictive-search__price">${escapeHtml(price)}</span></span>` : ''}
            </span>
          </a>
        </li>
      `;
    };

    const renderResults = (query, results) => {
      if (!resultsEl) return;

      const products = results && results.products ? results.products : [];
      const collections = results && results.collections ? results.collections : [];
      const pages = results && results.pages ? results.pages : [];
      const articles = results && results.articles ? results.articles : [];

      if (products.length + collections.length + pages.length + articles.length === 0) {
        const message = noResultsTemplate.replace('__QUERY__', query);
        resultsEl.innerHTML = '';
        if (statusEl) statusEl.textContent = message;
        return;
      }

      if (statusEl) statusEl.textContent = '';

      const html = [
        renderGroup(productsLabel, products, renderProductItem),
        renderGroup(collectionsLabel, collections, renderLinkItem),
        renderGroup(pagesLabel, pages, renderLinkItem),
        renderGroup(articlesLabel, articles, renderLinkItem),
      ].join('');

      resultsEl.innerHTML = html;
    };

    const fetchSuggestions = debounce(async () => {
      if (!(input instanceof HTMLInputElement)) return;
      const query = input.value.trim();

      if (!query) {
        if (resultsEl) resultsEl.innerHTML = '';
        if (statusEl) statusEl.textContent = startText;
        if (abortController) abortController.abort();
        abortController = null;
        return;
      }

      if (!suggestUrl) return;

      if (statusEl) statusEl.textContent = '';

      if (abortController) abortController.abort();
      abortController = new AbortController();

      try {
        const url = new URL(suggestUrl, window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', types);
        url.searchParams.set('resources[limit]', String(limit));
        url.searchParams.set('resources[options][unavailable_products]', 'hide');

        const response = await fetch(url.toString(), {
          signal: abortController.signal,
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) throw new Error('Predictive search request failed');

        const data = await response.json();
        const results = data && data.resources && data.resources.results ? data.resources.results : null;
        renderResults(query, results);
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        console.error('Predictive search error:', error);
      }
    }, 180);

    if (input instanceof HTMLInputElement) {
      input.addEventListener('input', fetchSuggestions);
    }
  }

  function initSearchTypeFilters() {
    const form = document.querySelector('.search-page__form');
    if (!form) return;

    const typeInput = form.querySelector('[data-search-type-input]');
    const radios = Array.from(form.querySelectorAll('[data-search-type-radio]'));

    if (!typeInput || radios.length === 0) return;

    const syncTypeInput = () => {
      const checked = radios.find(radio => radio.checked);
      const value = checked?.value || '';

      if (!value) {
        typeInput.value = '';
        typeInput.disabled = true;
        return;
      }

      typeInput.disabled = false;
      typeInput.value = value;
    };

    radios.forEach(radio => {
      radio.addEventListener('change', syncTypeInput);
    });

    form.addEventListener('submit', () => {
      // The hidden `type` input carries the real filter; avoid submitting a
      // redundant `search_type` param.
      radios.forEach(radio => {
        radio.disabled = true;
      });
    });

    syncTypeInput();
  }

  function initSearchPage() {
    const searchSection = document.querySelector('[data-search-section]');
    if (!searchSection) return;
    if (searchSection.dataset.searchPageBound === 'true') return;
    searchSection.dataset.searchPageBound = 'true';

    const filterPills = Array.from(searchSection.querySelectorAll('[data-search-type-radio]'));
    const typeInput = searchSection.querySelector('[data-search-type-input]');
    const searchForm = searchSection.querySelector('[data-search-form]');

    const setPillActive = (activePill) => {
      filterPills.forEach(pill => pill.classList.toggle('is-active', pill === activePill));
    };

    const setType = (value) => {
      if (!(typeInput instanceof HTMLInputElement)) return;

      if (value) {
        typeInput.value = value;
        typeInput.disabled = false;
      } else {
        typeInput.value = '';
        typeInput.disabled = true;
      }
    };

    const submitIfHasQuery = () => {
      if (!searchForm) return;
      const searchInput = searchSection.querySelector('[data-search-input]');
      if (searchInput instanceof HTMLInputElement && searchInput.value.trim()) {
        searchForm.submit();
      }
    };

    const viewButtons = Array.from(searchSection.querySelectorAll('[data-view]'));
    const resultsContainer = searchSection.querySelector('[data-results-container]');

    const setView = (view, activeBtn) => {
      viewButtons.forEach(btn => btn.classList.toggle('is-active', btn === activeBtn));
      if (resultsContainer) resultsContainer.setAttribute('data-view', view);
    };

    searchSection.addEventListener('click', async (event) => {
      const pill = event.target.closest('[data-search-type-radio]');
      if (pill instanceof HTMLElement && filterPills.includes(pill)) {
        const value = pill.dataset.value || '';
        setPillActive(pill);
        setType(value);
        submitIfHasQuery();
        return;
      }

      const viewBtn = event.target.closest('[data-view]');
      if (viewBtn instanceof HTMLElement && viewButtons.includes(viewBtn)) {
        const view = viewBtn.dataset.view || 'grid';
        setView(view, viewBtn);
        return;
      }
    });

    // Animate results on load
    const results = Array.from(searchSection.querySelectorAll('[data-search-result]'));
    results.forEach((result, index) => {
      if (!(result instanceof HTMLElement)) return;
      result.style.animationDelay = `${index * 0.05}s`;
      result.classList.add('animate-in');
    });
  }

  // ==========================================================================
  // QUICK ADD
  // ==========================================================================

  function initQuickAddButtons() {
    if (document.documentElement.dataset.quickAddBound === 'true') return;
    document.documentElement.dataset.quickAddBound = 'true';

    document.addEventListener('click', async (event) => {
      const btn = event.target.closest('[data-quick-add][data-variant-id]');
      if (!(btn instanceof HTMLButtonElement)) return;

      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      event.preventDefault();
      event.stopPropagation();

      const originalContent = btn.innerHTML;
      btn.disabled = true;
      btn.innerHTML = '<span class="loading-spinner" aria-hidden="true"></span>';

      try {
        await addToCart(variantId, 1);
        await refreshCart();

        btn.textContent = `✓ ${themeString('added', 'Added!')}`;
        btn.classList.add('is-success');

        if (window.openCartDrawer) window.openCartDrawer(btn);

        setTimeout(() => {
          btn.innerHTML = originalContent;
          btn.classList.remove('is-success');
          btn.disabled = false;
        }, 2000);
      } catch (error) {
        console.error('Error adding to cart:', error);
        btn.innerHTML = originalContent;
        btn.disabled = false;
      }
    });
  }

  // ==========================================================================
  // PRODUCT CARD SWATCHES
  // ==========================================================================

  function initProductCardSwatches() {
    if (document.documentElement.dataset.productCardSwatchesBound === 'true') return;
    document.documentElement.dataset.productCardSwatchesBound = 'true';

    document.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-swatch-btn]');
      if (!(btn instanceof HTMLButtonElement)) return;

      const container = btn.closest('[data-swatch-container]');
      if (!(container instanceof HTMLElement)) return;

      event.preventDefault();
      event.stopPropagation();

      container.querySelectorAll('[data-swatch-btn]').forEach((el) => {
        el.classList.remove('is-active');
        if (el instanceof HTMLButtonElement) el.setAttribute('aria-pressed', 'false');
      });

      btn.classList.add('is-active');
      btn.setAttribute('aria-pressed', 'true');

      const imageUrl = btn.dataset.imageUrl;
      if (!imageUrl) return;

      const card = btn.closest('.product-card');
      if (!card) return;

      const image = card.querySelector('.product-card__image--primary');
      if (!(image instanceof HTMLImageElement)) return;

      image.setAttribute('src', imageUrl);
      if (image.hasAttribute('srcset')) image.removeAttribute('srcset');
    });
  }

  // ==========================================================================
  // COLLECTION FILTERS + SORT
  // ==========================================================================

  function initCollectionFilters() {
    const form = document.querySelector('[data-collection-filters-form]');
    if (!(form instanceof HTMLFormElement)) return;
    if (form.dataset.collectionFiltersBound === 'true') return;
    form.dataset.collectionFiltersBound = 'true';

    const submit = () => {
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.submit();
      }
    };

    const debouncedSubmit = debounce(submit, 450);

    form.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

      if (target.matches('[data-price-range-input]')) {
        debouncedSubmit();
        return;
      }

      if (target.matches('input[type="checkbox"], select')) {
        submit();
      }
    });

    form.addEventListener('input', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (!target.matches('[data-price-range-input]')) return;

      debouncedSubmit();
    });
  }

  // ==========================================================================
  // CART DRAWER
  // ==========================================================================

  function initCartDrawer() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;

    const content = drawer.querySelector('.cart-drawer__content');
    const closeButtons = drawer.querySelectorAll('[data-cart-drawer-close]');
    const cartButtons = document.querySelectorAll('[data-cart-btn], .header__cart-btn');

    let lastActiveElement = null;

    const setTriggerExpanded = (isExpanded) => {
      cartButtons.forEach(btn => {
        btn.setAttribute('aria-expanded', String(isExpanded));
      });
    };

    const focusCloseButton = () => {
      const closeButton = drawer.querySelector('.cart-drawer__close');
      if (!closeButton) return;
      closeButton.focus();
    };

    function openDrawer(trigger = null) {
      lastActiveElement = trigger || document.activeElement;
      drawer.classList.add('is-open');
      setInteractiveHiddenState(drawer, false);
      setTriggerExpanded(true);
      document.body.style.overflow = 'hidden';

      requestAnimationFrame(focusCloseButton);
    }

    function closeDrawer() {
      drawer.classList.remove('is-open');
      setInteractiveHiddenState(drawer, true);
      setTriggerExpanded(false);
      document.body.style.overflow = '';

      if (lastActiveElement instanceof HTMLElement) {
        lastActiveElement.focus();
      }
      lastActiveElement = null;
    }

    // Make available globally (used by other scripts/handlers)
    window.openCartDrawer = openDrawer;
    window.closeCartDrawer = closeDrawer;

    // Bind global handlers only once
    if (!window.__themeCartDrawerEventsBound) {
      window.__themeCartDrawerEventsBound = true;

      // Open drawer on cart button click
      cartButtons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          if (window.openCartDrawer) window.openCartDrawer(btn);
        });
      });

      // Close on escape
      document.addEventListener('keydown', (e) => {
        const currentDrawer = document.querySelector('[data-cart-drawer]');
        if (e.key === 'Escape' && currentDrawer && currentDrawer.classList.contains('is-open')) {
          if (window.closeCartDrawer) window.closeCartDrawer();
        }
      });
    }

    // Close handlers
    closeButtons.forEach(btn => {
      if (btn.dataset.cartDrawerCloseBound === 'true') return;
      btn.dataset.cartDrawerCloseBound = 'true';
      btn.addEventListener('click', () => {
        if (window.closeCartDrawer) window.closeCartDrawer();
      });
    });

    // Focus trap (bind once to the persistent drawer element)
    if (drawer.dataset.cartDrawerFocusTrapBound !== 'true') {
      drawer.dataset.cartDrawerFocusTrapBound = 'true';

      drawer.addEventListener('keydown', (event) => {
        if (!drawer.classList.contains('is-open')) return;
        trapFocusWithin(content || drawer, event);
      });
    }

    setInteractiveHiddenState(drawer, true);

    // Initialize quantity controls
    initCartQuantityControls(drawer);

    // Initialize remove buttons
    initCartRemoveButtons(drawer);

    // Initialize upsell buttons
    initCartUpsellButtons(drawer);

    // Initialize free gift + add-ons
    initCartFreeGiftButtons(drawer);
    initCartGiftWrapToggle(drawer);
  }

  function initCartQuantityControls(container) {
    container.querySelectorAll('[data-cart-item]').forEach(item => {
      const minusBtn = item.querySelector('[data-quantity-minus]');
      const plusBtn = item.querySelector('[data-quantity-plus]');
      const input = item.querySelector('[data-quantity-input]');
      const key = item.dataset.key;

      if (!minusBtn || !plusBtn || !input) return;

      minusBtn.addEventListener('click', () => {
        const newQty = Math.max(0, parseInt(input.value) - 1);
        updateCartItem(key, newQty, item);
      });

      plusBtn.addEventListener('click', () => {
        const newQty = parseInt(input.value) + 1;
        updateCartItem(key, newQty, item);
      });

      input.addEventListener('change', () => {
        const newQty = Math.max(0, parseInt(input.value) || 0);
        updateCartItem(key, newQty, item);
      });
    });
  }

  function initCartRemoveButtons(container) {
    container.querySelectorAll('[data-remove-item]').forEach(btn => {
      btn.addEventListener('click', () => {
        const item = btn.closest('[data-cart-item]');
        if (item) {
          updateCartItem(item.dataset.key, 0, item);
        }
      });
    });
  }

  function initCartUpsellButtons(container) {
    container.querySelectorAll('[data-upsell-add]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const variantId = btn.dataset.upsellAdd;
        const originalText = btn.textContent;
        btn.disabled = true;
        btn.textContent = '...';

        try {
          await addToCart(variantId, 1);
          await refreshCart();
        } catch (error) {
          console.error('Error adding upsell:', error);
        } finally {
          if (btn.isConnected) {
            btn.disabled = false;
            btn.textContent = originalText;
          }
        }
      });
    });
  }

  function initCartFreeGiftButtons(container) {
    container.querySelectorAll('[data-free-gift-add]').forEach(btn => {
      if (!(btn instanceof HTMLButtonElement)) return;
      if (btn.dataset.freeGiftBound === 'true') return;
      btn.dataset.freeGiftBound = 'true';

      btn.addEventListener('click', async () => {
        const variantId = btn.dataset.freeGiftAdd;
        if (!variantId) return;

        const originalText = btn.textContent;
        btn.disabled = true;
        btn.textContent = '...';

        try {
          await addToCart(variantId, 1, { properties: { _free_gift: 'true' } });
          await refreshCart();
        } catch (error) {
          console.error('Error adding free gift:', error);
          if (btn.isConnected) {
            btn.disabled = false;
            btn.textContent = originalText;
          }
        }
      });
    });
  }

  function initCartGiftWrapToggle(container) {
    const toggle = container.querySelector('[data-gift-wrap-toggle]');
    if (!(toggle instanceof HTMLInputElement)) return;
    if (toggle.dataset.giftWrapBound === 'true') return;
    toggle.dataset.giftWrapBound = 'true';

    toggle.addEventListener('change', async () => {
      const variantId = toggle.dataset.variantId;
      if (!variantId) return;

      const shouldAdd = toggle.checked;
      toggle.disabled = true;

      try {
        if (shouldAdd) {
          await addToCart(variantId, 1, { properties: { _gift_wrap: 'true' } });
          await refreshCart();
          return;
        }

        const key = toggle.dataset.lineKey;
        if (key) {
          await updateCartItem(key, 0);
          return;
        }

        // Fallback: if we don't have the line key, refresh to re-sync UI
        await refreshCart();
      } catch (error) {
        console.error('Error updating gift wrap:', error);
        toggle.checked = !shouldAdd;
        toggle.disabled = false;
      }
    });
  }

  async function updateCartItem(key, quantity, itemElement) {
    try {
      if (quantity === 0 && itemElement) {
        itemElement.classList.add('is-removing');
      }

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });

      if (!response.ok) throw new Error('Failed to update cart');

      setTimeout(() => refreshCart(), 300);
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  }

  async function addToCartFromFormData(formData) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      body: formData
    });

    if (!response.ok) throw new Error('Failed to add to cart');
    return response.json();
  }

  async function addItemsToCart(items = []) {
    const normalized = Array.isArray(items)
      ? items
          .map(item => ({
            id: item && item.id ? String(item.id) : null,
            quantity: item && item.quantity ? Number(item.quantity) : 1,
            selling_plan: item && item.sellingPlanId ? String(item.sellingPlanId) : undefined,
            properties: item && item.properties && typeof item.properties === 'object' ? item.properties : undefined
          }))
          .filter(item => item.id && Number.isFinite(item.quantity) && item.quantity > 0)
      : [];

    if (normalized.length === 0) throw new Error('No items to add');

    const payload = {
      items: normalized.map(item => {
        const entry = { id: item.id, quantity: item.quantity };
        if (item.selling_plan) entry.selling_plan = item.selling_plan;
        if (item.properties) entry.properties = item.properties;
        return entry;
      })
    };

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!response.ok) throw new Error('Failed to add items to cart');
    return response.json();
  }

  async function addToCart(variantId, quantity = 1, options = {}) {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);

    if (options && options.sellingPlanId) {
      formData.append('selling_plan', String(options.sellingPlanId));
    }

    if (options && options.properties && typeof options.properties === 'object') {
      Object.entries(options.properties).forEach(([key, value]) => {
        if (value == null) return;
        formData.append(`properties[${key}]`, String(value));
      });
    }

    return addToCartFromFormData(formData);
  }

  // ==========================================================================
  // MULTI-ADD (BUNDLES / FBT)
  // ==========================================================================

  function getMultiAddItemVariantId(item) {
    const select = item.querySelector('[data-multi-add-variant]');
    if (select instanceof HTMLSelectElement && select.value) return select.value;

    const input = item.querySelector('[data-multi-add-variant-id]');
    if (input instanceof HTMLInputElement && input.value) return input.value;

    return null;
  }

  function getMultiAddItemQuantity(item) {
    const input = item.querySelector('[data-multi-add-quantity]');
    if (!(input instanceof HTMLInputElement)) return 1;

    const raw = Number.parseInt(input.value || '1', 10);
    return Number.isFinite(raw) && raw > 0 ? raw : 1;
  }

  function getMultiAddItemPriceCents(item) {
    const select = item.querySelector('[data-multi-add-variant]');
    if (select instanceof HTMLSelectElement) {
      const option = select.selectedOptions && select.selectedOptions[0];
      if (option) {
        const cents = Number(option.dataset.price);
        if (Number.isFinite(cents)) return cents;
      }
    }

    const cents = Number(item.dataset.priceCents);
    return Number.isFinite(cents) ? cents : 0;
  }

  function syncMultiAddItemDisplay(item) {
    const priceEl = item.querySelector('[data-multi-add-item-price]');
    if (!priceEl) return;

    const cents = getMultiAddItemPriceCents(item);
    priceEl.textContent = formatMoney(cents);
  }

  function syncMultiAddTotals(container) {
    const totalEl = container.querySelector('[data-multi-add-total]');
    if (!totalEl) return;

    let total = 0;

    container.querySelectorAll('[data-multi-add-item]').forEach(item => {
      if (!(item instanceof HTMLElement)) return;

      const checkbox = item.querySelector('[data-multi-add-check]');
      if (checkbox instanceof HTMLInputElement && !checkbox.checked) return;

      const cents = getMultiAddItemPriceCents(item);
      const quantity = getMultiAddItemQuantity(item);
      total += cents * quantity;
    });

    totalEl.textContent = formatMoney(total);
  }

  function initMultiAdd() {
    if (document.documentElement.dataset.multiAddBound === 'true') return;
    document.documentElement.dataset.multiAddBound = 'true';

    const syncFromTarget = (target) => {
      const container = target.closest('[data-multi-add]');
      if (!(container instanceof HTMLElement)) return;

      const item = target.closest('[data-multi-add-item]');
      if (item instanceof HTMLElement) syncMultiAddItemDisplay(item);

      syncMultiAddTotals(container);
    };

    document.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

      if (
        !target.matches('[data-multi-add-check]') &&
        !target.matches('[data-multi-add-variant]') &&
        !target.matches('[data-multi-add-quantity]')
      ) {
        return;
      }

      syncFromTarget(target);
    });

    document.addEventListener('click', async (event) => {
      const btn = event.target.closest('[data-multi-add-submit]');
      if (!(btn instanceof HTMLButtonElement)) return;

      const container = btn.closest('[data-multi-add]');
      if (!(container instanceof HTMLElement)) return;

      event.preventDefault();
      event.stopPropagation();

      const items = [];

      container.querySelectorAll('[data-multi-add-item]').forEach(item => {
        if (!(item instanceof HTMLElement)) return;

        const checkbox = item.querySelector('[data-multi-add-check]');
        if (checkbox instanceof HTMLInputElement && !checkbox.checked) return;

        const variantId = getMultiAddItemVariantId(item);
        if (!variantId) return;

        items.push({
          id: variantId,
          quantity: getMultiAddItemQuantity(item)
        });
      });

      const originalText = btn.textContent;
      btn.disabled = true;
      btn.textContent = '...';

      try {
        await addItemsToCart(items);
        await refreshCart();

        btn.textContent = '✓';

        if (window.openCartDrawer) {
          setTimeout(() => window.openCartDrawer(btn), 300);
        }

        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 2000);
      } catch (error) {
        console.error('Error adding bundle items:', error);
        btn.textContent = originalText;
        btn.disabled = false;
      }
    });

    document.querySelectorAll('[data-multi-add]').forEach(container => {
      if (container instanceof HTMLElement) {
        container.querySelectorAll('[data-multi-add-item]').forEach(item => {
          if (item instanceof HTMLElement) syncMultiAddItemDisplay(item);
        });
        syncMultiAddTotals(container);
      }
    });
  }

  async function refreshCartDrawer() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return false;

    try {
      const response = await fetch('/?section_id=cart-drawer');
      if (!response.ok) throw new Error('Failed to render cart drawer');

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newDrawer = doc.querySelector('[data-cart-drawer]');

      if (!newDrawer) return false;

      drawer.innerHTML = newDrawer.innerHTML;
      initCartDrawer();
      return true;
    } catch (error) {
      console.error('Error refreshing cart drawer:', error);
      return false;
    }
  }

  async function refreshCart() {
    const [drawerOk] = await Promise.all([refreshCartDrawer(), updateCartCount()]);
    if (!drawerOk) location.reload();
  }

  // ==========================================================================
  // PRODUCT FORM
  // ==========================================================================

  function formatMoney(cents, format) {
    const moneyFormat = format || (window.theme && window.theme.moneyFormat) || '${{amount}}';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, moneyFormat);
    }

    const value = (Number(cents) / 100).toFixed(2);
    return moneyFormat.replace('{{amount}}', value);
  }

  function getProductVariants(section) {
    if (!section) return null;
    if (section.__themeVariants) return section.__themeVariants;

    const jsonEl = section.querySelector('[data-product-variants]');
    if (!jsonEl) return null;

    try {
      section.__themeVariants = JSON.parse(jsonEl.textContent);
      return section.__themeVariants;
    } catch (error) {
      console.error('Invalid product variants JSON', error);
      return null;
    }
  }

  function getSellingPlanAllocations(section) {
    if (!section) return null;
    if (section.__themeSellingPlanAllocations) return section.__themeSellingPlanAllocations;

    const jsonEl = section.querySelector('[data-selling-plan-allocations]');
    if (!jsonEl) return null;

    try {
      section.__themeSellingPlanAllocations = JSON.parse(jsonEl.textContent);
      return section.__themeSellingPlanAllocations;
    } catch (error) {
      console.error('Invalid selling plan allocations JSON', error);
      return null;
    }
  }

  function getPlansForVariant(section, variantId) {
    const allocations = getSellingPlanAllocations(section);
    if (!allocations || typeof allocations !== 'object') return [];
    if (!allocations.variants || typeof allocations.variants !== 'object') return [];

    const plans = allocations.variants[String(variantId)];
    return Array.isArray(plans) ? plans.filter(plan => plan && plan.id) : [];
  }

  function updatePurchaseOptions(section, variant) {
    if (!section || !variant) return;

    const container = section.querySelector('[data-purchase-options]');
    if (!container) return;

    const onetimeRadio = container.querySelector('[data-purchase-option="onetime"]');
    const subscriptionRadio = container.querySelector('[data-purchase-option="subscription"]');
    const planContainer = container.querySelector('[data-selling-plan-container]');
    const planSelect = container.querySelector('[data-selling-plan-select]');

    const onetimePriceEl = container.querySelector('[data-onetime-price]');
    const subscriptionPriceEl = container.querySelector('[data-subscription-price]');

    const plans = getPlansForVariant(section, variant.id);
    const hasPlans = plans.length > 0;

    if (onetimePriceEl) onetimePriceEl.textContent = formatMoney(variant.price);

    if (subscriptionRadio instanceof HTMLInputElement) {
      subscriptionRadio.disabled = !hasPlans;
      subscriptionRadio.setAttribute('aria-disabled', String(!hasPlans));
    }

    if (!hasPlans && onetimeRadio instanceof HTMLInputElement) {
      onetimeRadio.checked = true;
    }

    // Build plan options when variant changes
    if (planSelect instanceof HTMLSelectElement) {
      const lastVariantId = section.__themeSellingPlanVariantId;
      if (String(lastVariantId || '') !== String(variant.id)) {
        const previousValue = planSelect.value;
        planSelect.innerHTML = '';

        plans.forEach(plan => {
          const option = document.createElement('option');
          option.value = String(plan.id);
          option.textContent = plan.name || String(plan.id);

          if (plan.price != null) option.dataset.price = String(plan.price);
          if (plan.compare_at_price != null) option.dataset.compareAtPrice = String(plan.compare_at_price);

          planSelect.appendChild(option);
        });

        if (previousValue) {
          const stillExists = Array.from(planSelect.options).some(opt => opt.value === previousValue);
          if (stillExists) planSelect.value = previousValue;
        }

        section.__themeSellingPlanVariantId = variant.id;
      }
    }

    // Sync subscription price display
    let subscriptionPrice = null;
    if (hasPlans) {
      const selectedId =
        planSelect instanceof HTMLSelectElement && planSelect.value ? planSelect.value : String(plans[0].id);
      const active = plans.find(plan => String(plan.id) === String(selectedId)) || plans[0];
      const cents = Number(active && active.price);
      if (Number.isFinite(cents) && cents > 0) subscriptionPrice = cents;
    }

    if (subscriptionPriceEl) {
      subscriptionPriceEl.textContent = subscriptionPrice != null ? formatMoney(subscriptionPrice) : formatMoney(variant.price);
    }

    const subscriptionBadge = container.querySelector('[data-subscription-badge]');
    if (subscriptionBadge instanceof HTMLElement) {
      const defaultText = subscriptionBadge.dataset.defaultText || subscriptionBadge.textContent;
      const base = Number(variant.price);

      if (subscriptionPrice != null && Number.isFinite(base) && base > subscriptionPrice) {
        const percent = Math.round(((base - subscriptionPrice) * 100) / base);
        subscriptionBadge.textContent = percent > 0 ? `-${percent}%` : defaultText;
      } else {
        subscriptionBadge.textContent = defaultText;
      }
    }

    // Show plans only when subscription is chosen
    const wantsSubscription = subscriptionRadio instanceof HTMLInputElement && subscriptionRadio.checked && hasPlans;

    if (planContainer instanceof HTMLElement) {
      planContainer.hidden = !wantsSubscription;
    }

    if (planSelect instanceof HTMLSelectElement) {
      planSelect.disabled = !wantsSubscription;
    }
  }

  function getSelectedVariant(section) {
    const variants = getProductVariants(section);
    if (!variants || variants.length === 0) return null;

    const groups = Array.from(section.querySelectorAll('[data-product-option-group]')).sort((a, b) => {
      const aPos = Number.parseInt(a.dataset.optionPosition || '0', 10);
      const bPos = Number.parseInt(b.dataset.optionPosition || '0', 10);
      return aPos - bPos;
    });

    const selections = groups.map(group => {
      const selected = group.querySelector('.product-form__option-btn.is-selected');
      return selected ? selected.dataset.optionValue : null;
    });

    const [option1, option2, option3] = selections;

    return (
      variants.find(variant => {
        if (option1 && variant.option1 !== option1) return false;
        if (option2 && variant.option2 !== option2) return false;
        if (option3 && variant.option3 !== option3) return false;
        return true;
      }) || null
    );
  }

  function setMainProductImage(section, thumbButton) {
    const mediaId = thumbButton.dataset.mediaId || thumbButton.dataset.imageId;
    if (!mediaId) return;

    section.querySelectorAll('[data-product-media]').forEach(el => {
      if (!(el instanceof HTMLElement)) return;
      const isActive = el.dataset.mediaId === mediaId;
      el.classList.toggle('is-active', isActive);
    });

    section.querySelectorAll('.product-gallery__thumb.is-active').forEach(el => {
      el.classList.remove('is-active');
      el.removeAttribute('aria-current');
    });

    thumbButton.classList.add('is-active');
    thumbButton.setAttribute('aria-current', 'true');
  }

  async function updatePickupAvailability(variantId) {
    const container = document.querySelector('[data-pickup-availability]');
    if (!(container instanceof HTMLElement)) return;

    const productUrl = container.dataset.productUrl;
    const sectionId = container.dataset.sectionId;
    if (!productUrl || !sectionId) return;

    const requestToken = String(variantId);
    container.dataset.loadingVariant = requestToken;

    try {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', String(variantId));
      url.searchParams.set('section_id', sectionId);

      const response = await fetch(url.toString());
      if (!response.ok) throw new Error('Failed to load pickup availability');

      const html = await response.text();
      if (container.dataset.loadingVariant !== requestToken) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-pickup-availability]');
      if (fresh) {
        container.innerHTML = fresh.innerHTML;
      }
    } catch (error) {
      // no-op
    } finally {
      if (container.dataset.loadingVariant === requestToken) {
        container.dataset.loadingVariant = '';
      }
    }
  }

  function updateStockUrgency(section, variant) {
    const urgency = section.querySelector('[data-stock-urgency]');
    if (!(urgency instanceof HTMLElement)) return;

    const lowThreshold = Number.parseInt(urgency.dataset.lowThreshold || '0', 10);
    const criticalThreshold = Number.parseInt(urgency.dataset.criticalThreshold || '3', 10);
    const maxStock = Number.parseInt(urgency.dataset.maxStock || '20', 10);
    const showBar = parseBoolean(urgency.dataset.showBar, true);
    const animate = parseBoolean(urgency.dataset.animate, true);

    const textHurry = urgency.dataset.textHurry || '';
    const textOnly = urgency.dataset.textOnly || '';
    const textOutOfStock = urgency.dataset.textOutOfStock || '';
    const textPreorder = urgency.dataset.textPreorder || '';
    const textHighDemand = urgency.dataset.textHighDemand || '';

    const inventoryManaged = Boolean(variant.inventory_management);
    const quantity = Number(variant.inventory_quantity);
    const allowsOversell = variant.inventory_policy === 'continue';

    let level = 'none';
    let shouldShow = false;
    let barPercent = 100;

    const safeMax = Number.isFinite(maxStock) && maxStock > 0 ? maxStock : 20;
    const toPercent = (value) => {
      const safeValue = Number.isFinite(value) ? value : 0;
      let percent = Math.round((safeValue * 100) / safeMax);
      if (percent > 100) percent = 100;
      if (percent < 0) percent = 0;
      if (percent < 5 && percent > 0) percent = 5;
      return percent;
    };

    if (inventoryManaged && variant.available) {
      if (allowsOversell && Number.isFinite(quantity) && quantity <= 0) {
        level = 'preorder';
        shouldShow = true;
      } else if (
        Number.isFinite(quantity) &&
        criticalThreshold > 0 &&
        quantity > 0 &&
        quantity <= criticalThreshold
      ) {
        level = 'critical';
        shouldShow = true;
        barPercent = toPercent(quantity);
      } else if (
        Number.isFinite(quantity) &&
        lowThreshold > 0 &&
        quantity > 0 &&
        quantity <= lowThreshold
      ) {
        level = 'low';
        shouldShow = true;
        barPercent = toPercent(quantity);
      } else {
        level = 'available';
        barPercent = toPercent(quantity);
      }
    } else if (!variant.available) {
      level = 'sold-out';
      shouldShow = true;
    }

    urgency.dataset.variantId = String(variant.id);
    urgency.hidden = !shouldShow;

    const levels = ['none', 'available', 'low', 'critical', 'sold-out', 'preorder'];
    levels.forEach((name) => urgency.classList.remove(`stock-urgency--${name}`));
    urgency.classList.add(`stock-urgency--${level}`);

    urgency.classList.remove('stock-urgency--pulse');
    if (level === 'critical' && animate) {
      urgency.classList.add('stock-urgency--pulse');
    }

    const textEl = urgency.querySelector('[data-stock-text]');
    if (textEl instanceof HTMLElement) {
      let message = '';
      if (level === 'critical') {
        message = textHurry.replace('__COUNT__', String(quantity));
      } else if (level === 'low') {
        message = textOnly.replace('__COUNT__', String(quantity));
      } else if (level === 'sold-out') {
        message = textOutOfStock;
      } else if (level === 'preorder') {
        message = textPreorder;
      }
      textEl.textContent = message;
    }

    const subEl = urgency.querySelector('[data-stock-sub]');
    if (subEl instanceof HTMLElement) {
      subEl.hidden = level !== 'critical';
      if (level === 'critical' && textHighDemand) subEl.textContent = textHighDemand;
    }

    const barEl = urgency.querySelector('[data-stock-bar]');
    if (barEl instanceof HTMLElement) {
      barEl.hidden = !showBar || level === 'sold-out' || level === 'preorder';
    }

    const fillEl = urgency.querySelector('[data-stock-fill]');
    if (fillEl instanceof HTMLElement) {
      fillEl.style.setProperty('--fill-percent', `${barPercent}%`);
    }
  }

  function applyVariantToProduct(section, variant) {
    if (!section || !variant) return;

    const variantInput = section.querySelector('[data-variant-id-input]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = String(variant.id);
    }

    const inventoryManaged = Boolean(variant.inventory_management);
    const quantity = Number(variant.inventory_quantity);
    const canPreorder =
      inventoryManaged &&
      variant.inventory_policy === 'continue' &&
      Number.isFinite(quantity) &&
      quantity <= 0 &&
      variant.available;

    const priceEl = section.querySelector('[data-product-price]');
    if (priceEl) priceEl.textContent = formatMoney(variant.price);

    const compareEl = section.querySelector('[data-product-compare-price]');
    const savingsEl = section.querySelector('[data-product-savings]');
    const compareAt = Number(variant.compare_at_price || 0);
    const price = Number(variant.price || 0);
    const isOnSale = compareAt > price;

    if (compareEl) {
      compareEl.hidden = !isOnSale;
      compareEl.textContent = isOnSale ? formatMoney(compareAt) : '';
    }

    if (savingsEl) {
      savingsEl.hidden = !isOnSale;
      savingsEl.textContent = isOnSale ? `${themeString('save', 'Save')} ${formatMoney(compareAt - price)}` : '';
    }

    const unitWrapper = section.querySelector('[data-product-unit-price-wrapper]');
    const unitPriceEl = section.querySelector('[data-product-unit-price]');
    const unitRefEl = section.querySelector('[data-product-unit-price-reference]');
    const unitPrice = Number(variant.unit_price || 0);
    const measurement = variant.unit_price_measurement;
    const hasUnitPricing = Boolean(measurement && unitPrice);

    if (unitWrapper instanceof HTMLElement) {
      unitWrapper.hidden = !hasUnitPricing;
    }

    if (hasUnitPricing) {
      if (unitPriceEl) unitPriceEl.textContent = formatMoney(unitPrice);

      if (unitRefEl && measurement) {
        const refValue = Number(measurement.reference_value || 1);
        const refUnit = measurement.reference_unit || '';
        unitRefEl.textContent = `${refValue !== 1 ? String(refValue) : ''}${refUnit}`;
      }
    }

    const submitBtn = section.querySelector('[data-product-submit]');
    const submitLabel = section.querySelector('[data-product-submit-label]');

    if (submitBtn instanceof HTMLButtonElement) {
      submitBtn.disabled = !variant.available;
      submitBtn.setAttribute('aria-disabled', String(!variant.available));
      submitBtn.dataset.isPreorder = String(canPreorder);
      submitBtn.classList.toggle('product-form__submit--preorder', canPreorder);

      const buttonsWrap = submitBtn.closest('.product-form__buttons');
      if (buttonsWrap instanceof HTMLElement) {
        buttonsWrap.classList.toggle('product-form__buttons--preorder', canPreorder);
      }

      const iconCart = submitBtn.querySelector('[data-submit-icon-cart]');
      if (iconCart instanceof HTMLElement) iconCart.hidden = canPreorder;

      const iconPreorder = submitBtn.querySelector('[data-submit-icon-preorder]');
      if (iconPreorder instanceof HTMLElement) iconPreorder.hidden = !canPreorder;
    }

    if (submitLabel) {
      if (!variant.available) {
        submitLabel.textContent = themeString('soldOut', 'Sold Out');
      } else if (canPreorder) {
        submitLabel.textContent = themeString('preorder', 'Pre-order');
      } else {
        submitLabel.textContent = themeString('addToCart', 'Add to Cart');
      }
    }

    const preorderNotice = section.querySelector('[data-preorder-notice]');
    if (preorderNotice instanceof HTMLElement) {
      preorderNotice.hidden = !canPreorder;
    }

    // Back in stock form (lead capture)
    const backInStock = section.querySelector('[data-back-in-stock]');
    if (backInStock instanceof HTMLElement) {
      const nextVariantId = String(variant.id);
      const prevVariantId = backInStock.dataset.variantId || '';
      const variantChanged = Boolean(prevVariantId && prevVariantId !== nextVariantId);

      backInStock.dataset.variantId = nextVariantId;
      backInStock.hidden = variant.available;

      if (variantChanged) {
        const rowEl = backInStock.querySelector('[data-back-in-stock-row]');
        if (rowEl instanceof HTMLElement) rowEl.hidden = false;

        const successEl = backInStock.querySelector('[data-back-in-stock-success]');
        if (successEl instanceof HTMLElement) successEl.hidden = true;

        const errorEl = backInStock.querySelector('[data-back-in-stock-error]');
        if (errorEl instanceof HTMLElement) {
          errorEl.hidden = true;
          errorEl.innerHTML = '';
        }
      }

      const returnToInput = backInStock.querySelector('[data-back-in-stock-return-to]');
      if (returnToInput instanceof HTMLInputElement) {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', String(variant.id));
          returnToInput.value = `${url.pathname}${url.search}`;
        } catch (error) {
          returnToInput.value = `${window.location.pathname}?variant=${variant.id}`;
        }
      }

      const bodyInput = backInStock.querySelector('[data-back-in-stock-body]');
      if (
        bodyInput instanceof HTMLInputElement ||
        bodyInput instanceof HTMLTextAreaElement
      ) {
        const productTitle = backInStock.dataset.productTitle || '';
        const variantTitle = variant.public_title || variant.title || '';

        let urlForBody = '';
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', String(variant.id));
          urlForBody = url.toString();
        } catch (error) {
          // no-op
        }

        const lines = [];
        if (productTitle) lines.push(`Product: ${productTitle}`);
        if (variantTitle) lines.push(`Variant: ${variantTitle}`);
        lines.push(`Variant ID: ${variant.id}`);
        if (urlForBody) lines.push(`URL: ${urlForBody}`);

        bodyInput.value = `Back in stock request:\n${lines.join('\n')}`;
      }
    }

    // Stock indicator
    const stockEl = section.querySelector('[data-product-stock]');
    if (stockEl instanceof HTMLElement) {
      const threshold = Number.parseInt(stockEl.dataset.lowStockThreshold || '0', 10);
      const inStockText = stockEl.dataset.textInStock || 'In stock';
      const outOfStockText = stockEl.dataset.textOutOfStock || 'Sold out';
      const preorderText = stockEl.dataset.textPreorder || 'Pre-order';
      const onlyLeftTemplate = stockEl.dataset.textOnlyLeft || 'Only __COUNT__ left';

      const canPreorderStock = inventoryManaged && variant.inventory_policy === 'continue' && Number.isFinite(quantity) && quantity <= 0;
      const isLowStock = inventoryManaged && Number.isFinite(quantity) && threshold > 0 && quantity > 0 && quantity <= threshold;

      let message = '';

      if (!variant.available) {
        message = outOfStockText;
      } else if (canPreorderStock) {
        message = preorderText;
      } else if (isLowStock) {
        message = onlyLeftTemplate.replace('__COUNT__', String(quantity));
      } else {
        message = inStockText;
      }

      stockEl.textContent = message;

      stockEl.classList.remove('is-in-stock', 'is-low-stock', 'is-out-of-stock', 'is-preorder');

      if (!variant.available) {
        stockEl.classList.add('is-out-of-stock');
      } else if (canPreorderStock) {
        stockEl.classList.add('is-preorder');
      } else if (isLowStock) {
        stockEl.classList.add('is-low-stock');
      } else {
        stockEl.classList.add('is-in-stock');
      }
    }

    updateStockUrgency(section, variant);

    // Variant-driven badges
    const badgesEl = section.querySelector('[data-product-badges]');
    if (badgesEl instanceof HTMLElement) {
      const saleStyle = badgesEl.dataset.saleBadgeStyle || 'percentage';
      const textSale = badgesEl.dataset.textSale || 'On sale';
      const textSave = badgesEl.dataset.textSave || 'Save';
      const textSoldOut = badgesEl.dataset.textSoldOut || 'Sold out';
      const textPreorder = badgesEl.dataset.textPreorder || 'Pre-order';
      const onlyLeftTemplate = badgesEl.dataset.textOnlyLeft || 'Only __COUNT__ left';

      const badgeSoldOut = badgesEl.querySelector('[data-product-badge="sold-out"]');
      const badgeSale = badgesEl.querySelector('[data-product-badge="sale"]');
      const badgePreorder = badgesEl.querySelector('[data-product-badge="preorder"]');
      const badgeLowStock = badgesEl.querySelector('[data-product-badge="low-stock"]');

      const compareAt = Number(variant.compare_at_price || 0);
      const price = Number(variant.price || 0);
      const onSale = Number.isFinite(compareAt) && Number.isFinite(price) && compareAt > price;

      const inventoryManaged = Boolean(variant.inventory_management);
      const quantity = Number(variant.inventory_quantity);
      const canPreorder = inventoryManaged && variant.inventory_policy === 'continue' && Number.isFinite(quantity) && quantity <= 0 && variant.available;

      const lowStockThreshold = Number.parseInt(section.querySelector('[data-product-stock]')?.dataset.lowStockThreshold || '0', 10);
      const isLowStock = inventoryManaged && Number.isFinite(quantity) && lowStockThreshold > 0 && quantity > 0 && quantity <= lowStockThreshold && variant.available;

      if (badgeSoldOut instanceof HTMLElement) {
        badgeSoldOut.hidden = variant.available;
        badgeSoldOut.textContent = textSoldOut;
      }

      if (badgePreorder instanceof HTMLElement) {
        badgePreorder.hidden = !canPreorder;
        badgePreorder.textContent = textPreorder;
      }

      if (badgeLowStock instanceof HTMLElement) {
        badgeLowStock.hidden = !isLowStock;
        badgeLowStock.textContent = isLowStock ? onlyLeftTemplate.replace('__COUNT__', String(quantity)) : '';
      }

      if (badgeSale instanceof HTMLElement) {
        badgeSale.hidden = !onSale;
        if (!onSale) {
          badgeSale.textContent = '';
        } else if (saleStyle === 'text') {
          badgeSale.textContent = textSale;
        } else if (saleStyle === 'amount') {
          badgeSale.textContent = `${textSave} ${formatMoney(compareAt - price)}`;
        } else {
          const percent = Math.round(((compareAt - price) * 100) / compareAt);
          badgeSale.textContent = `-${percent}%`;
        }
      }
    }

    updatePurchaseOptions(section, variant);

    // Sync any multi-add "current product" items (e.g., FBT)
    const currentMultiAddItems = Array.from(document.querySelectorAll('[data-multi-add-current]'));
    if (currentMultiAddItems.length > 0) {
      const containers = new Set();
      currentMultiAddItems.forEach(item => {
        if (!(item instanceof HTMLElement)) return;
        item.dataset.priceCents = String(price);

        const idInput = item.querySelector('[data-multi-add-variant-id]');
        if (idInput instanceof HTMLInputElement) {
          idInput.value = String(variant.id);
        }

        syncMultiAddItemDisplay(item);

        const container = item.closest('[data-multi-add]');
        if (container instanceof HTMLElement) {
          const submitBtn = container.querySelector('[data-multi-add-submit]');
          if (submitBtn instanceof HTMLButtonElement) {
            const disabled = !variant.available;
            submitBtn.disabled = disabled;
            submitBtn.setAttribute('aria-disabled', String(disabled));
          }

          containers.add(container);
        }
      });

      containers.forEach(container => syncMultiAddTotals(container));
    }

    const stickyBar = document.querySelector('[data-sticky-atc]');
    if (stickyBar) {
      const stickyAddBtn = stickyBar.querySelector('[data-sticky-add]');
      if (stickyAddBtn instanceof HTMLButtonElement) {
        stickyAddBtn.dataset.stickyAdd = String(variant.id);
        stickyAddBtn.disabled = !variant.available;
        if (!variant.available) {
          stickyAddBtn.textContent = themeString('soldOut', 'Sold Out');
        } else if (canPreorder) {
          stickyAddBtn.textContent = themeString('preorder', 'Pre-order');
        } else {
          stickyAddBtn.textContent = themeString('addToCart', 'Add to Cart');
        }
      }

      const stickyPrice = stickyBar.querySelector('.sticky-atc__price-current');
      if (stickyPrice) stickyPrice.textContent = formatMoney(price);

      const stickyCompare = stickyBar.querySelector('.sticky-atc__price-compare');
      if (stickyCompare) {
        stickyCompare.textContent = isOnSale ? formatMoney(compareAt) : '';
        stickyCompare.hidden = !isOnSale;
      }
    }

    // Variant image (sync to gallery thumbnails if possible)
    if (variant.featured_image && variant.featured_image.id) {
      const thumb = section.querySelector(`[data-image-id="${variant.featured_image.id}"]`);
      if (thumb instanceof HTMLElement) {
        setMainProductImage(section, thumb);
      }
    }

    // Update URL on product page (avoid doing this in embedded contexts)
    if (document.body.classList.contains('template-product')) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState({}, '', url.toString());
      } catch (error) {
        // no-op
      }
    }

    updatePickupAvailability(variant.id);
  }

  function initProductOptionButtons() {
    if (document.documentElement.dataset.productOptionsBound === 'true') return;
    document.documentElement.dataset.productOptionsBound = 'true';

    document.addEventListener('click', (event) => {
      const optionBtn = event.target.closest('.product-form__option-btn[data-option-position][data-option-value]');
      if (!(optionBtn instanceof HTMLElement)) return;

      const section = optionBtn.closest('[data-product-section]');
      if (!section) return;

      const group = optionBtn.closest('[data-product-option-group]');
      if (!group) return;

      group.querySelectorAll('.product-form__option-btn').forEach(btn => {
        btn.classList.remove('is-selected');
        btn.setAttribute('aria-pressed', 'false');
      });

      optionBtn.classList.add('is-selected');
      optionBtn.setAttribute('aria-pressed', 'true');

      const variant = getSelectedVariant(section);
      if (!variant) return;
      applyVariantToProduct(section, variant);
    });
  }

  function initPurchaseOptions() {
    if (document.documentElement.dataset.purchaseOptionsBound === 'true') return;
    document.documentElement.dataset.purchaseOptionsBound = 'true';

    document.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const purchaseOptions = target.closest('[data-purchase-options]');
      if (!purchaseOptions) return;

      const section = purchaseOptions.closest('[data-product-section]');
      if (!section) return;

      if (
        !(target instanceof HTMLInputElement) &&
        !(target instanceof HTMLSelectElement)
      ) {
        return;
      }

      if (
        target instanceof HTMLInputElement &&
        target.name !== 'purchase_option'
      ) {
        return;
      }

      if (
        target instanceof HTMLSelectElement &&
        !target.matches('[data-selling-plan-select]')
      ) {
        return;
      }

      const variant = getSelectedVariant(section);
      if (!variant) return;

      updatePurchaseOptions(section, variant);
    });
  }

  // ==========================================================================
  // ASYNC SECTION LOADERS (RECOMMENDATIONS / FBT)
  // ==========================================================================

  async function replaceSectionInnerHTML(container, selector, url) {
    if (!url) return false;

    const state = container.dataset.asyncState;
    if (state === 'loading' || state === 'loaded') return false;

    container.dataset.asyncState = 'loading';

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to load section');

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector(selector);

      if (fresh) {
        container.innerHTML = fresh.innerHTML;
        container.dataset.asyncState = 'loaded';
        container.dataset.performed = 'true';
        return true;
      }
    } catch (error) {
      console.error('Error loading section:', error);
    }

    container.dataset.asyncState = '';
    return false;
  }

  function initProductRecommendationsSections() {
    document.querySelectorAll('[data-product-recommendations]').forEach(container => {
      if (!(container instanceof HTMLElement)) return;

      const performed = container.dataset.performed === 'true';
      if (performed) return;

      const url = container.dataset.url;
      replaceSectionInnerHTML(container, '[data-product-recommendations]', url);
    });
  }

  function initFrequentlyBoughtTogetherSections() {
    document.querySelectorAll('[data-fbt]').forEach(container => {
      if (!(container instanceof HTMLElement)) return;

      const performed = container.dataset.performed === 'true';
      if (performed) return;

      const url = container.dataset.url;
      replaceSectionInnerHTML(container, '[data-fbt]', url).then(updated => {
        if (!updated) return;

        const multiAdd = container.querySelector('[data-multi-add]');
        if (!(multiAdd instanceof HTMLElement)) {
          const section = container.closest('section');
          if (section instanceof HTMLElement) section.hidden = true;
          return;
        }

        const productSection = document.querySelector('[data-product-section]');
        const currentVariant = productSection ? getSelectedVariant(productSection) : null;
        if (currentVariant) {
          const currentItem = multiAdd.querySelector('[data-multi-add-current]');
          if (currentItem instanceof HTMLElement) {
            currentItem.dataset.priceCents = String(currentVariant.price);
            const idInput = currentItem.querySelector('[data-multi-add-variant-id]');
            if (idInput instanceof HTMLInputElement) {
              idInput.value = String(currentVariant.id);
            }
          }
        }

        const submitBtn = multiAdd.querySelector('[data-multi-add-submit]');
        if (submitBtn instanceof HTMLButtonElement && currentVariant) {
          const disabled = !currentVariant.available;
          submitBtn.disabled = disabled;
          submitBtn.setAttribute('aria-disabled', String(disabled));
        }

        multiAdd.querySelectorAll('[data-multi-add-item]').forEach(item => {
          if (item instanceof HTMLElement) syncMultiAddItemDisplay(item);
        });
        syncMultiAddTotals(multiAdd);
      });
    });
  }

  function syncVolumeBreaks(section) {
    const container = section.querySelector('[data-volume-breaks]');
    if (!container) return;

    const qtyInput = section.querySelector('form.product-form [data-quantity-input]');
    if (!(qtyInput instanceof HTMLInputElement)) return;

    const raw = Number.parseInt(qtyInput.value || '1', 10);
    const qty = Number.isFinite(raw) && raw > 0 ? raw : 1;

    container.querySelectorAll('[data-volume-qty]').forEach(btn => {
      if (!(btn instanceof HTMLElement)) return;
      const targetQty = Number.parseInt(btn.dataset.volumeQty || '0', 10);
      btn.classList.toggle('is-selected', Number.isFinite(targetQty) && targetQty === qty);
    });
  }

  function initVolumeBreaks() {
    if (document.documentElement.dataset.volumeBreaksBound === 'true') return;
    document.documentElement.dataset.volumeBreaksBound = 'true';

    document.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-volume-qty]');
      if (!(btn instanceof HTMLElement)) return;

      const section = btn.closest('[data-product-section]');
      if (!section) return;

      const raw = Number.parseInt(btn.dataset.volumeQty || '0', 10);
      const qty = Number.isFinite(raw) && raw > 0 ? raw : null;
      if (!qty) return;

      const qtyInput = section.querySelector('form.product-form [data-quantity-input]');
      if (!(qtyInput instanceof HTMLInputElement)) return;

      qtyInput.value = String(qty);
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      syncVolumeBreaks(section);
    });

    document.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (!input.matches('form.product-form [data-quantity-input]')) return;

      const section = input.closest('[data-product-section]');
      if (!section) return;
      syncVolumeBreaks(section);
    });

    document.querySelectorAll('[data-product-section]').forEach(section => {
      syncVolumeBreaks(section);
    });
  }

  function initProductGalleryThumbnails() {
    if (document.documentElement.dataset.productGalleryBound === 'true') return;
    document.documentElement.dataset.productGalleryBound = 'true';

    document.addEventListener('click', (event) => {
      const thumb = event.target.closest('.product-gallery__thumb[data-media-id]');
      if (!(thumb instanceof HTMLElement)) return;

      const section = thumb.closest('[data-product-section]');
      if (!section) return;

      setMainProductImage(section, thumb);
    });
  }

  function initGiftCardRecipientForm() {
    if (document.documentElement.dataset.giftRecipientBound === 'true') return;
    document.documentElement.dataset.giftRecipientBound = 'true';

    document.addEventListener('change', (event) => {
      const toggle = event.target;
      if (!(toggle instanceof HTMLInputElement)) return;
      if (!toggle.matches('[data-gift-recipient-toggle]')) return;

      const container = toggle.closest('[data-gift-card-recipient]');
      if (!(container instanceof HTMLElement)) return;

      const fields = container.querySelector('[data-gift-recipient-fields]');
      if (fields instanceof HTMLElement) {
        fields.hidden = !toggle.checked;
      }

      const email = container.querySelector('[data-gift-recipient-email]');
      if (email instanceof HTMLInputElement) {
        email.required = toggle.checked;
      }

      const productSection = toggle.closest('[data-product-section]');
      if (productSection instanceof HTMLElement) {
        const dynamicCheckout = productSection.querySelector('[data-dynamic-checkout]');
        if (dynamicCheckout instanceof HTMLElement) {
          dynamicCheckout.hidden = toggle.checked;
        }
      }
    });
  }

  function syncInitialProductVariants() {
    document.querySelectorAll('[data-product-section]').forEach(section => {
      const variants = getProductVariants(section);
      if (!variants || variants.length === 0) return;

      const variant = getSelectedVariant(section);
      if (!variant) return;

      applyVariantToProduct(section, variant);
    });
  }

  function initQuantitySelectors() {
    const syncQuantitySelector = (selector) => {
      const input = selector.querySelector('[data-quantity-input]');
      const minusBtn = selector.querySelector('[data-quantity-minus]');
      const plusBtn = selector.querySelector('[data-quantity-plus]');

      if (!input || !minusBtn || !plusBtn) return;

      const min = Number.parseInt(input.min || '1', 10);
      const max = input.max ? Number.parseInt(input.max, 10) : Number.POSITIVE_INFINITY;

      const minValue = Number.isFinite(min) ? min : 1;
      const maxValue = Number.isFinite(max) ? max : Number.POSITIVE_INFINITY;

      const current = Number.parseInt(input.value || String(minValue), 10);
      const currentValue = Number.isFinite(current) ? current : minValue;

      const nextValue = Math.min(maxValue, Math.max(minValue, currentValue));
      input.value = String(nextValue);

      const minusDisabled = nextValue <= minValue;
      const plusDisabled = nextValue >= maxValue;

      minusBtn.disabled = minusDisabled;
      minusBtn.setAttribute('aria-disabled', String(minusDisabled));

      plusBtn.disabled = plusDisabled;
      plusBtn.setAttribute('aria-disabled', String(plusDisabled));
    };

    for (const selector of document.querySelectorAll('[data-quantity-selector]')) {
      syncQuantitySelector(selector);
    }

    document.addEventListener('click', (event) => {
      const minus = event.target.closest('[data-quantity-minus]');
      const plus = event.target.closest('[data-quantity-plus]');
      const button = minus || plus;
      if (!button) return;

      const selector = button.closest('[data-quantity-selector]');
      if (!selector) return;

      const input = selector.querySelector('[data-quantity-input]');
      if (!input) return;

      const min = Number.parseInt(input.min || '1', 10);
      const max = input.max ? Number.parseInt(input.max, 10) : Number.POSITIVE_INFINITY;

      const minValue = Number.isFinite(min) ? min : 1;
      const maxValue = Number.isFinite(max) ? max : Number.POSITIVE_INFINITY;

      const current = Number.parseInt(input.value || String(minValue), 10);
      const currentValue = Number.isFinite(current) ? current : minValue;

      const delta = minus ? -1 : 1;
      const nextValue = Math.min(maxValue, Math.max(minValue, currentValue + delta));
      input.value = String(nextValue);

      input.dispatchEvent(new Event('change', { bubbles: true }));
      syncQuantitySelector(selector);
    });

    document.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (!input.matches('[data-quantity-input]')) return;

      const selector = input.closest('[data-quantity-selector]');
      if (!selector) return;

      syncQuantitySelector(selector);
    });
  }

  function initStickyATC() {
    const stickyBar = document.querySelector('[data-sticky-atc]');
    if (!stickyBar) return;

    const scrollTopButton = document.querySelector('[data-scroll-top]');
    const whatsappButton = document.querySelector('[data-whatsapp-float]');

    const toggleFloatingOffsets = (isVisible) => {
      [scrollTopButton, whatsappButton].forEach((button) => {
        if (!(button instanceof HTMLElement)) return;
        button.classList.toggle('has-sticky-cart', isVisible);
      });
    };

    const show = () => {
      stickyBar.classList.add('is-visible');
      setInteractiveHiddenState(stickyBar, false);
      toggleFloatingOffsets(true);
    };

    const hide = () => {
      stickyBar.classList.remove('is-visible');
      setInteractiveHiddenState(stickyBar, true);
      toggleFloatingOffsets(false);
    };

    const target = document.querySelector('.product-form__submit') || document.querySelector('.product-form');
    if (!target) return;

    const syncVisibility = () => {
      const rect = target.getBoundingClientRect();
      if (rect.bottom < 0) {
        show();
        return;
      }

      hide();
    };

    const handleScroll = debounce(syncVisibility, 50);

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    syncVisibility();

    const quantityInput = stickyBar.querySelector('[data-sticky-quantity]');

    const addBtn = stickyBar.querySelector('[data-sticky-add]');
    if (!addBtn) return;

    addBtn.addEventListener('click', async () => {
      const variantId = addBtn.dataset.stickyAdd;
      if (!variantId) return;

      const qty = Number.parseInt(quantityInput?.value || '1', 10);
      const quantity = Number.isFinite(qty) && qty > 0 ? qty : 1;

      const sellingPlanSelect = document.querySelector(
        '[data-purchase-options] [data-selling-plan-select]:not([disabled])'
      );
      const sellingPlanId =
        sellingPlanSelect instanceof HTMLSelectElement && sellingPlanSelect.value
          ? sellingPlanSelect.value
          : null;

      const originalText = addBtn.textContent;
      addBtn.disabled = true;
      addBtn.textContent = '...';

      try {
        await addToCart(variantId, quantity, { sellingPlanId });
        await refreshCart();

        addBtn.textContent = '✓';

        if (window.openCartDrawer) {
          setTimeout(() => window.openCartDrawer(), 500);
        }

        setTimeout(() => {
          addBtn.textContent = originalText;
          addBtn.disabled = false;
        }, 2000);
      } catch (error) {
        console.error('Error adding to cart:', error);
        addBtn.textContent = originalText;
        addBtn.disabled = false;
      }
    });
  }

  function initProductForms() {
    initAjaxAddToCartForms();
  }

  function initAjaxAddToCartForms() {
    const cartAjaxEnabled = parseBoolean(document.documentElement.dataset.cartAjax, true);
    if (!cartAjaxEnabled) return;

    const forms = document.querySelectorAll('form[action*="/cart/add"]');

    forms.forEach(form => {
      if (form.dataset.ajaxCartInitialized === 'true') return;
      form.dataset.ajaxCartInitialized = 'true';

      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const formData = new FormData(form);
        const variantId = formData.get('id');

        if (!variantId) {
          form.submit();
          return;
        }

        let quantity = parseInt(formData.get('quantity') || '1', 10);
        if (!Number.isFinite(quantity) || quantity < 1) quantity = 1;
        formData.set('quantity', String(quantity));

        const submitBtn =
          form.querySelector('.product-form__submit') ||
          form.querySelector('button[type="submit"], input[type="submit"]');

        const isInput = submitBtn && submitBtn.tagName === 'INPUT';
        const originalText = submitBtn ? (isInput ? submitBtn.value : submitBtn.innerHTML) : null;

        if (submitBtn) {
          submitBtn.disabled = true;
          if (isInput) {
            submitBtn.value = themeString('adding', 'Adding...');
          } else {
            submitBtn.innerHTML = `<span class="loading-spinner" aria-hidden="true"></span> ${themeString('adding', 'Adding...')}`;
          }
        }

        try {
          await addToCartFromFormData(formData);

          if (submitBtn) {
            if (isInput) {
              submitBtn.value = `✓ ${themeString('added', 'Added!')}`;
            } else {
              submitBtn.innerHTML = `✓ ${themeString('added', 'Added!')}`;
            }
          }

          await refreshCart();

          if (window.openCartDrawer) window.openCartDrawer();

          setTimeout(() => {
            if (submitBtn) {
              if (isInput) {
                submitBtn.value = originalText || submitBtn.value;
              } else {
                submitBtn.innerHTML = originalText || submitBtn.innerHTML;
              }
              submitBtn.disabled = false;
            }
          }, 2000);
        } catch (error) {
          console.error('Error adding to cart:', error);
          if (submitBtn) {
            if (isInput) {
              submitBtn.value = themeString('error', 'Error');
            } else {
              submitBtn.innerHTML = themeString('error', 'Error');
            }
          }
          setTimeout(() => {
            if (submitBtn) {
              if (isInput) {
                submitBtn.value = originalText || submitBtn.value;
              } else {
                submitBtn.innerHTML = originalText || submitBtn.innerHTML;
              }
              submitBtn.disabled = false;
            }
          }, 2000);
        }
      });
    });
  }

  async function updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
      });
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  // ==========================================================================
  // SCROLL TO TOP
  // ==========================================================================

  function initScrollToTop() {
    const btn = document.querySelector('[data-scroll-top]');
    if (!btn) return;

    const handleScroll = debounce(() => {
      if (window.scrollY > 500) {
        btn.classList.add('is-visible');
      } else {
        btn.classList.remove('is-visible');
      }
    }, 100);

    window.addEventListener('scroll', handleScroll, { passive: true });

    btn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  // ==========================================================================
  // RECENTLY VIEWED
  // ==========================================================================

  function initRecentlyViewed() {
    const container = document.querySelector('[data-recently-viewed]');
    if (!container) return;

    const productId = container.dataset.currentProduct;
    const storageKey = 'recently_viewed_products';
    const maxItems = 8;

    // Get existing items
    let items = JSON.parse(localStorage.getItem(storageKey) || '[]');

    // Add current product if on product page
    if (productId && !items.includes(productId)) {
      items.unshift(productId);
      items = items.slice(0, maxItems);
      localStorage.setItem(storageKey, JSON.stringify(items));
    }
  }

  // ==========================================================================
  // BEFORE/AFTER SLIDERS
  // ==========================================================================

  function initBeforeAfterSliders() {
    document.querySelectorAll('[data-before-after]').forEach(section => {
      if (!(section instanceof HTMLElement)) return;
      if (section.dataset.beforeAfterBound === 'true') return;
      section.dataset.beforeAfterBound = 'true';

      const range = section.querySelector('[data-before-after-range]');
      if (!(range instanceof HTMLInputElement)) return;

      const sync = () => {
        const raw = Number(range.value);
        const value = Number.isFinite(raw) ? Math.min(100, Math.max(0, raw)) : 50;
        section.style.setProperty('--before-after-position', `${value}%`);
      };

      range.addEventListener('input', sync);
      sync();
    });
  }

  // ==========================================================================
  // COMPLIANCE (COOKIE / AGE GATE)
  // ==========================================================================

  function initCookieConsent() {
    const banner = document.querySelector('[data-cookie-consent]');
    if (!(banner instanceof HTMLElement)) return;
    if (window.Shopify && window.Shopify.designMode) return;

    const storageKey = 'aurum_cookie_consent';

    try {
      if (localStorage.getItem(storageKey) === '1') return;
    } catch (error) {
      // localStorage unavailable, still show banner
    }

    banner.hidden = false;

    const acceptBtn = banner.querySelector('[data-cookie-accept]');
    if (acceptBtn instanceof HTMLButtonElement) {
      acceptBtn.addEventListener('click', () => {
        try {
          localStorage.setItem(storageKey, '1');
        } catch (error) {
          // ignore
        }
        banner.hidden = true;
      });
    }
  }

  function initAgeGate() {
    const gate = document.querySelector('[data-age-gate]');
    if (!(gate instanceof HTMLElement)) return;
    if (window.Shopify && window.Shopify.designMode) return;

    const storageKey = 'aurum_age_gate_verified';
    try {
      if (localStorage.getItem(storageKey) === '1') return;
    } catch (error) {
      // localStorage unavailable, still show gate
    }

    const dialog = gate.querySelector('.age-gate__dialog');
    if (!(dialog instanceof HTMLElement)) return;

    const acceptBtn = gate.querySelector('[data-age-accept]');
    const declineBtn = gate.querySelector('[data-age-decline]');

    const lastActive = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const close = () => {
      gate.hidden = true;
      document.body.style.overflow = '';
      if (lastActive) lastActive.focus();
    };

    const open = () => {
      gate.hidden = false;
      document.body.style.overflow = 'hidden';
      if (acceptBtn instanceof HTMLButtonElement) acceptBtn.focus();
    };

    gate.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      if (gate.hidden) return;

      const focusableSelectors = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',');

      const focusables = Array.from(dialog.querySelectorAll(focusableSelectors)).filter(el => {
        if (!(el instanceof HTMLElement)) return false;
        return el.getClientRects().length > 0;
      });

      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    if (acceptBtn instanceof HTMLButtonElement) {
      acceptBtn.addEventListener('click', () => {
        try {
          localStorage.setItem(storageKey, '1');
        } catch (error) {
          // ignore
        }
        close();
      });
    }

    if (declineBtn instanceof HTMLButtonElement) {
      declineBtn.addEventListener('click', () => {
        const url = gate.dataset.declineUrl;
        if (url) {
          window.location.href = url;
          return;
        }

        const message = gate.querySelector('[data-age-decline-message]');
        if (message instanceof HTMLElement) {
          message.hidden = false;
        }
      });
    }

    open();
  }

  // ==========================================================================
  // INIT
  // ==========================================================================

  function init() {
    initHeader();
    initHeaderMegaMenu();
    initMobileMenu();
    initThemeToggle();
    initTestimonialsCarousel();
    initAccordions();
    initScrollAnimations();
    initSmoothScroll();
    initCookieConsent();
    initAgeGate();
    initSearchDrawer();
    initSearchPage();
    initCollectionFilters();
    initCartDrawer();
    initQuickAddButtons();
    initProductCardSwatches();
    initMultiAdd();
    initProductRecommendationsSections();
    initFrequentlyBoughtTogetherSections();
    initQuantitySelectors();
    initProductOptionButtons();
    initPurchaseOptions();
    initVolumeBreaks();
    initProductGalleryThumbnails();
    initGiftCardRecipientForm();
    syncInitialProductVariants();
    initProductForms();
    initStickyATC();
    initScrollToTop();
    initRecentlyViewed();
    initBeforeAfterSliders();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
